import React, { useState } from "react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../../components/ui/AlertDialog";
import { Button } from "../../../../components/ui/Button";
import { Info, ChevronLeft, ChevronRight, X } from "lucide-react";

interface FallbackInfoItem {
  content: string;
  graphTitle: string;
  graphUrl: string;
  id: number;
  needFallback: boolean;
}

interface FallbackInformationDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  fallbackInfo: FallbackInfoItem[];
  onClose: () => void;
}

const FallbackInformationDialog: React.FC<FallbackInformationDialogProps> = ({
  isOpen,
  onOpenChange,
  fallbackInfo,
  onClose,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % fallbackInfo.length);
  };

  const handlePrev = () => {
    setCurrentIndex(
      (prev) => (prev - 1 + fallbackInfo.length) % fallbackInfo.length
    );
  };

  const handleClose = () => {
    setCurrentIndex(0);
    onClose();
    onOpenChange(false);
  };

  React.useEffect(() => {
    if (!isOpen) {
      setCurrentIndex(0);
    }
  }, [isOpen]);

  if (fallbackInfo.length === 0) {
    return null;
  }

  const currentItem = fallbackInfo[currentIndex];

  return (
    <AlertDialog open={isOpen} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-2xl max-h-[80vh] overflow-hidden">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-blue-700">
            <Info className="h-5 w-5" />
            <span>Additional Information</span>
          </AlertDialogTitle>
          <AlertDialogDescription>
            Information {currentIndex + 1} of {fallbackInfo.length}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="flex-1 overflow-y-auto">
          <div className="space-y-4">
            {/* Information Content */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-2">
                Information #{currentIndex + 1}
              </h3>
              <p className="text-gray-700 leading-relaxed">
                {currentItem.content}
              </p>
            </div>

            {/* Graph Section (if available) */}
            {currentItem.graphTitle && (
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-blue-900 mb-2">
                  Graph: {currentItem.graphTitle}
                </h4>
                {currentItem.graphUrl ? (
                  <div className="bg-white p-2 rounded border">
                    <img
                      src={currentItem.graphUrl}
                      alt={currentItem.graphTitle}
                      className="w-full h-auto max-h-64 object-contain"
                    />
                  </div>
                ) : (
                  <p className="text-blue-700 italic">
                    Graph image not available
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Navigation Controls */}
        <div className="flex items-center justify-between pt-4 border-t">
          <Button
            onClick={handlePrev}
            variant="outline"
            size="sm"
            disabled={fallbackInfo.length <= 1}
            className="flex items-center gap-1"
          >
            <ChevronLeft className="h-4 w-4" />
            Previous
          </Button>

          <div className="flex items-center gap-2">
            {fallbackInfo.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-2 h-2 rounded-full transition-colors ${
                  index === currentIndex
                    ? "bg-blue-600"
                    : "bg-gray-300 hover:bg-gray-400"
                }`}
              />
            ))}
          </div>

          <Button
            onClick={handleNext}
            variant="outline"
            size="sm"
            disabled={fallbackInfo.length <= 1}
            className="flex items-center gap-1"
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {/* Close Button */}
        <div className="flex justify-end pt-2">
          <Button
            onClick={handleClose}
            variant="outline"
            size="sm"
            className="flex items-center gap-1"
          >
            <X className="h-4 w-4" />
            Close
          </Button>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default FallbackInformationDialog;
